import { useState } from 'react';
import type { ReactNode } from 'react';
import { AlertTriangle, AtSign, Ban, Bell, History, KeyRound, ListChecks, Lock, Radar, ScrollText, ShieldCheck, Siren, SlidersHorizontal, Sparkles, Timer } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { TabsTruot } from '../../../components/shared';
import { useT } from '../../../i18n';
import { BangNhatKy } from '../../nhat-ky/components/BangNhatKy';
import { KhoiSuDungAi } from '../../nhat-ky-truy-vet/su-dung-ai/KhoiSuDungAi';
import { NguonVetTenant } from '../../nhat-ky-truy-vet/su-dung-ai/nguon';
import { useFormCaiDat } from '../hooks/useFormCaiDat';
import { giaTriHienTai, KHOA_EMAIL_DOMAIN } from '../nghiep-vu/cai-dat';
import type { FormCaiDat } from '../hooks/useFormCaiDat';
import { BangChanIp } from './BangChanIp';
import { BaoCaoIpMonitor } from './BaoCaoIpMonitor';
import { KhoiCaiDat, LoiTaiCaiDat } from './form/KhoiCaiDat';
import { KhoiCanhBaoAudit } from './KhoiCanhBaoAudit';
import { KhoiKhoaTam } from './KhoiKhoaTam';
import { KhoiMucMfa } from './KhoiMucMfa';
import { ThanhLuuCaiDat } from './form/ThanhLuuCaiDat';
import { LichSuThayDoi } from './LichSuThayDoi';

/** Thẻ chứa tham số — chỉ những thẻ này mới có thanh lưu bên dưới. */
const THE_CO_FORM = new Set(['truy-cap-ip', 'dang-nhap', 'nhat-ky']);

/**
 * Thẻ con của "Truy cập IP" không có ô nhập: blocklist chặn ngay khi bấm, báo cáo IP ngoài dải chỉ đưa IP vào bản
 * nháp. Thanh lưu chỉ hiện ở đây khi bản nháp có thay đổi thật, để không ai tưởng phải bấm Lưu sau khi chặn, và một
 * thanh "Lưu" tắt không chiếm chỗ ở thẻ không có gì để lưu.
 */
const THE_CON_KHONG_FORM = new Set(['ip-ngoai-dai', 'ip-bi-chan']);

/** Thẻ mở được từ `?the=` — đích chuyển hướng của các màn Nhật ký & Truy vết cũ (`routes/index.tsx`). */
const THE_TU_URL = new Set(['nhat-ky', 'su-dung-ai']);

/**
 * Ba thẻ "Cài đặt bảo mật" của một tenant (ADR-0059 mục 8), cộng thẻ "Lịch sử
 * thay đổi". Không có thẻ giới hạn truy cập: trần request cố định ở Gateway,
 * tenant không thấy (ADR-0087). **Một** bộ component cho hai cổng:
 *
 * - Cổng tenant (`laNenTang = false`): Tenant Admin, chỉ được siết.
 * - Tab "Bảo mật" của `TenantDetailView` (`laNenTang = true`): Platform Admin,
 *   thấy thêm tham số chỉ họ sửa — tỉ lệ audit, gỡ lỗi.
 *
 * Thẻ "Đăng nhập" ở cổng tenant thay ô `auth.mfa_required` bằng bộ chọn ba mức
 * xác thực hai lớp (`KhoiMucMfa`, ADR-0047) — một màn, một nút lưu cho cả hai mức.
 *
 * Thẻ "Nhật ký sử dụng AI" chỉ có ở cổng tenant: cổng nền tảng xem mục này cho
 * mọi tenant ở màn Nhật ký hệ thống.
 *
 * Ai sửa được dòng nào do **danh mục** quyết (`editable` của API), không do cờ
 * ở đây; cờ chỉ quyết thẻ nào có khối tham số nào.
 *
 * Bản nháp sống ở `useFormCaiDat` **trên** hàng thẻ (xem chú thích ở đó), nên
 * sửa ở thẻ này rồi sang thẻ khác không mất, và một nút lưu đếm đủ mọi thẻ.
 */
export function CaiDatBaoMat({
  tenantId,
  laNenTang,
  duocSua,
}: {
  tenantId: string;
  laNenTang: boolean;
  duocSua: boolean;
}) {
  const { t } = useT();
  const form = useFormCaiDat({ loai: 'tenant', id: tenantId }, { chiSiet: !laNenTang, chiDoc: !duocSua });
  const [params] = useSearchParams();
  // Mọi tenant đều có, không theo gói (ADR-UI-0017): module `audit` không thuộc dịch vụ nào nên không bị cắt.
  const coSuDungAi = !laNenTang;
  const [the, setThe] = useState(() => {
    const xin = laNenTang ? null : params.get('the');
    return xin && THE_TU_URL.has(xin) && (xin !== 'su-dung-ai' || coSuDungAi) ? xin : 'truy-cap-ip';
  });
  const [theIp, setTheIp] = useState('dai-tin-cay');
  const ip = form.policy?.caller_ip ?? null;
  const hienThanhLuu =
    THE_CO_FORM.has(the) && !(the === 'truy-cap-ip' && THE_CON_KHONG_FORM.has(theIp) && form.thayDoi.length === 0);

  /*
   * ADR-0099: đang (hoặc sắp) cho phép email ngoài domain đã xác minh thì nói hậu quả ngay dưới công tắc: người dùng
   * dùng được email cá nhân, và cả tổ chức bắt buộc xác thực hai lớp.
   */
  const mucEmail = form.policy?.settings.find((m) => m.key === KHOA_EMAIL_DOMAIN);
  const canhBaoEmail = (khoa: string) =>
    khoa === KHOA_EMAIL_DOMAIN && mucEmail && giaTriHienTai(mucEmail, form.nhap) === false ? (
      <p
        role="status"
        className="flex items-start gap-2 rounded border border-warning-200 bg-warning-50 px-3 py-2 text-xs leading-relaxed text-warning-700"
      >
        <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden />
        {t('baoMat.dangNhap.emailNgoaiDomainCanhBao')}
      </p>
    ) : null;

  const nhan = (icon: ReactNode, chu: string) => (
    <span className="flex items-center gap-1.5">
      {icon}
      {chu}
    </span>
  );

  const theNhatKy = (f: FormCaiDat) => (
    <div className="space-y-4">
      <KhoiCaiDat
        form={f}
        icon={<Bell size={16} />}
        tieuDe={t('baoMat.nhatKy.canhBaoTieuDe')}
        khoa={['notify.tenant_security', 'notify.audit_alert']}
      />
      <KhoiCaiDat
        form={f}
        icon={<Siren size={16} />}
        tieuDe={t('quyTacCanhBao.tieuDe')}
        khoa={['audit.alert_rules']}
      />
      <KhoiCanhBaoAudit tenantId={tenantId} duocSua={duocSua} />
      {laNenTang && (
        <KhoiCaiDat
          form={f}
          icon={<SlidersHorizontal size={16} />}
          tieuDe={t('baoMat.nhatKy.vanHanhTieuDe')}
          khoa={[
            { tieuDe: t('caiDat.nhom.ghiNhatKy'), khoa: ['audit.allow_sample_rate', 'audit.read_sample_rate'] },
            { tieuDe: t('caiDat.nhom.goLoi'), khoa: ['log.debug_until'] },
          ]}
        />
      )}
      <BangNhatKy tenantId={tenantId} nenTang={laNenTang} />
    </div>
  );

  return (
    <div className="space-y-4">
      {form.loiTai ? <LoiTaiCaiDat form={form} /> : null}

      <TabsTruot
        size="small"
        xuongDong
        defaultActiveKey={the}
        onChange={setThe}
        items={[
          {
            key: 'truy-cap-ip',
            label: nhan(<ShieldCheck size={14} />, t('baoMat.the.truyCapIp')),
            children: (
              /*
                Bốn thẻ con (ADR-0098) thay cột khối dài: dải tin cậy kèm OTP ngoài dải, báo cáo IP ngoài dải, blocklist,
                chặn tạm tự động. Dải tin cậy và chặn tạm tự động là tham số, lưu qua thanh lưu chung; blocklist chặn
                ngay khi bấm.
              */
              <TabsTruot
                size="small"
                gon
                xuongDong
                defaultActiveKey={theIp}
                onChange={setTheIp}
                items={[
                  {
                    key: 'dai-tin-cay',
                    label: nhan(<ListChecks size={14} />, t('baoMat.theIp.daiTinCay')),
                    children: (
                      <KhoiCaiDat
                        form={form}
                        icon={<ListChecks size={16} />}
                        tieuDe={t('baoMat.ip.allowlistTieuDe')}
                        khoa={['network.ip_allowlist', 'network.mfa_ngoai_dai']}
                      />
                    ),
                  },
                  {
                    key: 'ip-ngoai-dai',
                    label: nhan(<Radar size={14} />, t('baoMat.theIp.ngoaiDai')),
                    children: <BaoCaoIpMonitor tenantId={tenantId} form={form} />,
                  },
                  {
                    key: 'ip-bi-chan',
                    label: nhan(<Ban size={14} />, t('baoMat.theIp.biChan')),
                    children: (
                      /*
                        Blocklist của tenant là bảng riêng có hạn, chặn ngay (ADR-0090 mục 3). Không tự khoá mình chỉ
                        kiểm ở cổng tenant: blocklist tenant không áp cổng nền tảng, Quản trị nền tảng vẫn gỡ được.
                      */
                      <BangChanIp
                        phamVi={{ loai: 'tenant', tenantId }}
                        duocSua={duocSua}
                        ipHienTai={ip}
                        kiemTuKhoa={!laNenTang}
                        nenTang={laNenTang}
                      />
                    ),
                  },
                  {
                    key: 'chan-tu-dong',
                    label: nhan(<Timer size={14} />, t('baoMat.theIp.tuDong')),
                    children: (
                      <KhoiCaiDat
                        form={form}
                        icon={<Timer size={16} />}
                        tieuDe={t('baoMat.ip.tuDongTieuDe')}
                        khoa={['network.auto_block_minutes']}
                      />
                    ),
                  },
                ]}
              />
            ),
          },
          {
            key: 'dang-nhap',
            label: nhan(<Lock size={14} />, t('baoMat.the.dangNhap')),
            children: (
              <div className="space-y-4">
                <KhoiCaiDat
                  form={form}
                  icon={<AtSign size={16} />}
                  tieuDe={t('baoMat.dangNhap.emailTieuDe')}
                  khoa={[KHOA_EMAIL_DOMAIN]}
                  duoi={canhBaoEmail}
                />
                {/*
                  Cổng tenant: danh sách chọn ba mức (ADR-0047) thay ô `auth.mfa_required`. Mức
                  từng người dùng tenant của token, nên cổng nền tảng giữ ô của form.
                */}
                {laNenTang ? (
                  <KhoiCaiDat
                    form={form}
                    icon={<KeyRound size={16} />}
                    tieuDe={t('baoMat.dangNhap.mfaTieuDe')}
                    khoa={['auth.mfa_required']}
                  />
                ) : (
                  <KhoiMucMfa tenantId={tenantId} form={form} duocSua={duocSua} />
                )}
                <KhoiCaiDat
                  form={form}
                  icon={<Lock size={16} />}
                  tieuDe={t('baoMat.dangNhap.matKhauTieuDe')}
                  khoa={[
                    {
                      tieuDe: t('caiDat.nhom.yeuCauMatKhau'),
                      khoa: [
                        'auth.password_min_length',
                        'auth.password_max_length',
                        'auth.password_history',
                        'auth.password_blacklist_enabled',
                      ],
                    },
                    {
                      // ADR-0078: khoá khi đăng nhập sai giữ ở nền tảng; tổ chức chỉ xem
                      tieuDe: t('caiDat.nhom.khoaKhiSai'),
                      khoa: ['auth.bruteforce_max_failures', 'auth.bruteforce_lock_minutes'],
                    },
                  ]}
                />
                <KhoiKhoaTam tenantId={tenantId} duocSua={duocSua} />
              </div>
            ),
          },
          {
            key: 'nhat-ky',
            label: nhan(<ScrollText size={14} />, t('baoMat.the.nhatKy')),
            children: theNhatKy(form),
          },
          ...(coSuDungAi
            ? [
                {
                  key: 'su-dung-ai',
                  label: nhan(<Sparkles size={14} />, t('nktvSuDungAi.tieuDe')),
                  children: (
                    <NguonVetTenant>
                      <KhoiSuDungAi />
                    </NguonVetTenant>
                  ),
                },
              ]
            : []),
          {
            key: 'lich-su',
            label: nhan(<History size={14} />, t('baoMat.the.lichSu')),
            children: <LichSuThayDoi tenantId={tenantId} form={form} duocSua={duocSua} />,
          },
        ]}
      />

      {!form.chiDoc && form.policy && hienThanhLuu && <ThanhLuuCaiDat form={form} />}
    </div>
  );
}
